import { FormEvent, useState } from "react";
import styled from "styled-components";
import {
  restrictedCountriesError,
  useRestrictedCountries,
} from "../../hooks/api/admin/useRestrictedCountries";
import { BACKGROUND_COLORS, BORDER_COLORS, COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";

const Card = styled.section`
  flex-shrink: 0;
  margin: ${SPACING.three}px 0;
  padding: ${SPACING.three}px;
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 8px;
  background: ${BACKGROUND_COLORS.componentSolid};
  h2 { margin: 0 0 8px; font-size: 16px; }
  p { color: ${TEXT_COLORS.two}; margin: 8px 0 16px; line-height: 1.5; }
  form { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
  input {
    box-sizing: border-box;
    width: 150px;
    height: 32px;
    border: 1px solid ${BORDER_COLORS.one};
    border-radius: 4px;
    padding: 0 8px;
    background: ${BACKGROUND_COLORS.four};
    color: ${TEXT_COLORS.one};
  }
`;

const CountryList = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(245px, 1fr));
  gap: 8px;
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 4px;
    background: ${BACKGROUND_COLORS.four};
  }
`;

const countryLabel = (code: string): string => {
  try {
    const name = new Intl.DisplayNames(["en"], { type: "region" }).of(code);
    return name && name !== code ? `${name} (${code})` : code;
  } catch {
    return code;
  }
};

export function RestrictedCountries() {
  const { data, error, isLoading, mutate, setRestrictedCountry } = useRestrictedCountries();
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const countryCode = input.trim().toUpperCase();
  const alreadyBlocked = data?.countries.includes(countryCode);
  const disabled = saving || isLoading || !data || Boolean(error);

  const update = async (code: string, restricted: boolean) => {
    const action = restricted ? "Block" : "Unblock";
    // eslint-disable-next-line no-alert
    if (!window.confirm(`${action} ${countryLabel(code)} in the production website geoblock?`)) return;
    setSaving(true);
    try {
      const updated = await setRestrictedCountry(code, restricted);
      if (updated && restricted) setInput("");
    } finally {
      setSaving(false);
    }
  };

  const addCountry = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled || !/^[A-Z]{2}$/.test(countryCode) || alreadyBlocked) return;
    update(countryCode, true);
  };

  return (
    <Card aria-labelledby="restricted-countries-title">
      <h2 id="restricted-countries-title">Blocked countries</h2>
      <p>
        Countries restricted by the production website geoblock. Changes take effect
        as the backend applies them; this list refreshes automatically.
      </p>
      <form onSubmit={addCountry}>
        <label htmlFor="restricted-country-code" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          Country code
          <input
            id="restricted-country-code"
            value={input}
            onChange={(event) => setInput(event.target.value.toUpperCase())}
            placeholder="e.g. SG"
            maxLength={2}
            autoComplete="off"
            disabled={disabled}
            aria-describedby="restricted-country-help"
          />
        </label>
        <Button
          type="submit"
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          disabled={disabled || !/^[A-Z]{2}$/.test(countryCode) || alreadyBlocked}
          style={{ height: 32, padding: "0 12px" }}
        >
          Add country
        </Button>
        <span id="restricted-country-help" style={{ color: TEXT_COLORS.two }}>
          {alreadyBlocked ? `${countryLabel(countryCode)} is already blocked.` : "Enter a two-letter country code."}
        </span>
      </form>
      {isLoading && <p role="status">Loading blocked countries…</p>}
      {error && (
        <div role="alert" style={{ color: COLORS.red.one, marginTop: 12 }}>
          Unable to load blocked countries: {restrictedCountriesError(error)}{" "}
          <Button type="button" onClick={() => mutate()} buttonTheme={ButtonThemeEnum.NEUTRAL2}>
            Retry
          </Button>
        </div>
      )}
      {!error && data?.countries.length === 0 && <p>No countries are blocked.</p>}
      {data && data.countries.length > 0 && (
        <CountryList aria-label="Blocked countries">
          {[...data.countries].sort().map((code) => (
            <li key={code}>
              <span>{countryLabel(code)}</span>
              <Button
                type="button"
                buttonTheme={ButtonThemeEnum.NEUTRAL2}
                disabled={disabled}
                aria-label={`Remove ${countryLabel(code)}`}
                onClick={() => update(code, false)}
                style={{ height: 28, padding: "0 8px" }}
              >
                Remove
              </Button>
            </li>
          ))}
        </CountryList>
      )}
    </Card>
  );
}
